import { PoUiTable1StyleAndContent } from "../models/data/po-relate.model"
import { SalesOrder, SalesOrderItem } from "../models/data/sales-order.model"
import { SKU } from "../models/data/sku.model"
import { UISortingOrder } from "../models/options/ui-sorting-order"
import { Entity } from "../models/props/entity"
import NP from 'number-precision'
/** update from salesIQ-ZY (eric 11/10/2021)
 * Remove any undefined/null value within an object.
 * Example: { a: null, b: undefined, c: 1 } will be cleaned to { c: 1 }
 * can be undefined
 * @param object
 * @returns
 */
export const cleanObject = (object: { [key: string]: any } | undefined) => {
  try {
    if (object) {
      return Object.keys(object).reduce((newObject, key) => object[key]
        ? { ...newObject, [key]: object[key] }
        : newObject
        , {})
    } else {
      return {}
    }
  } catch (error) {
    console.error(error)
    return {}
  }
}


/**
 * Used in array.prototype.find/findIndex/ to determine
 * if an entity's ID matched given ID
 * @param id string | number
 * @param field the field for comparison
 * @returns boolean
 */
export const matchEntity =
  (id: string | number, field: string = "id") =>
    (entity: Entity): boolean => {
      try {
        return entity[field] === id
      } catch (error) {
        console.error(error)
        return false
      }
    }

/**
* Used in array.prototype.filter() to determine
* if an entry is not empty string, null or undefined
* @param entity any type
* @returns (entry: any) => boolean
*/
export const notEmptyNullUndefined =
  (entity: any): boolean => {
    try {
      return entity !== undefined && entity !== null && entity?.toString().length > 0
    } catch (e) {
      console.error(e)
      return false
    }
  }

/**
* Used in array.prototype.filter() to determine
* if an entry is not null or undefined
* @param entity any type
* @returns (entry: any) => boolean
*/
export const notNullUndefined =
  (entity: any): boolean => {
    try {
      return entity !== undefined && entity !== null
    } catch (e) {
      console.error(e)
      return false
    }
  }

/**
* Receive terms and construct matching function.
* @param terms
* @param strict if match should be case-sensitive
* @returns Entity => boolean
*/
export const entityMatchTerms =
  (terms: string[], strict: boolean = false) =>

    /**
     * If any term within the terms array matches any field of entity, return true
     * 5/28/2021 update SO and SO item will not search the id relevant fields
     * @param entity
     * @returns boolean
     */
    (entity: Entity): boolean => {
      try {
        return terms.length
          ? terms.some(term => {
            return Object.values({ ...entity })
              .flat(1)
              .filter(entry => entry)
              .map(entry => entry.toString())
              .map(entry => strict ? entry : entry.toLowerCase())
              .some(entry => entry.indexOf(strict ? term : term.toLowerCase()) !== -1)
          }
          )
          : true
      } catch (error) {
        console.error(error)
        return false
      }
    }

/**
 * search the sku by name (can be multi keywords)
 * @param terms keyword split by spacke
 * @returns boolean of the search result
 */
export const skuNameMatchTerms =
  (terms: string[]) =>
    (sku: SKU): boolean => {
      try {
        return terms.every(term => (`${sku.name} ${sku.code} ${sku.asianName}`).toUpperCase().indexOf(term.toUpperCase()) > -1)
      }
      catch (error) {
        console.error(error)
        return false
      }
    }





/**
 * Outer function receives key for comparison and order.
 * Inner function receives left and right element.
 * Used in Array.prototype.sort().
 * @param key
 * @param order
 * @returns
 */
export const compareByKeyInOrder =
  (key: string, order: UISortingOrder) =>
    (left: Entity, right: Entity): number => {
      try {
        const [leftValueOfKey, rightValueOfKey] = [
          convertValueForComparison(left[key]),
          convertValueForComparison(right[key])
        ]

        if (leftValueOfKey < rightValueOfKey) {
          return order === UISortingOrder.ASCENDING ? -1 : 1
        } else if (leftValueOfKey === rightValueOfKey) {
          return 0
        } else {
          return order === UISortingOrder.ASCENDING ? 1 : -1
        }
      } catch (error) {
        console.error(error)
        return 0
      }

    }

/**
* Transforms value into comparable type.
* @param value
* @returns string | number
*/
export const convertValueForComparison = (value: any): string | number => {
  try {
    switch (typeof value) {
      case "string": return value
      case "number": return value
      default: return value?.toString()
    }
  } catch (error) {
    console.error(error)
    return value.toString()
  }
}

/**
 * Accummulate order level bigint totals.
 * @param items SalesOrderItem[]
 * @param field field name of item
 */
export const calculateTotalBigInt =
  (items: SalesOrderItem[], field: string): string => {
    try {
      const total = items.reduce((acc, cur) => acc + BigInt(parseInt(cur[field].toString())), BigInt(0))
      return total.toString()
    } catch (error) {
      console.error(error)
      return null
    }
  }

/**
* Does exactly as insertCommaIntoNumberString() but with regex magic.
* Taken from https://stackoverflow.com/a/494046
*/
export const insertCommaIntoNumberStringRegex =
  (value: string): string => {
    try {
      return value.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    } catch (error) {
      return null
    }
  }

/**
 * Used in array.prototype.find/findIndex/ to determine
 * if an entity's ID contain given ID
 * @param id string | number
 * @param field the field for comparison
 * @returns boolean
 */
export const IncludeKeyWords =
  (id: string | number, field: string = "id") =>
    (so: SalesOrder): boolean => {
      try {
        return so[field]?.toUpperCase().indexOf(id) !== -1
      } catch (error) {
        console.error(error)
        return false
      }
    }


export const isPostiveInteger = (value: any): boolean => {
  return !isNaN(value) && parseInt(value) == value && parseInt(value) >= 0 && !isNaN(parseInt(value, 10));
}

export const isValidEditPrice = (value: any): boolean => {
  return (value !== undefined && value !== null && parseFloat(value) === value && parseFloat(value) >= 0) || (value === undefined || value === null)
}

/**
 * check two array is same, reference: https://segmentfault.com/a/1190000016574183
 * @param a array of any type
 * @param b array of any type
 * @returns boolean
 */
export const arrayEqual = (a, b): boolean => {
  // 判断数组的长度
  if (a.length !== b.length) {
    return false
  } else {
    // 循环遍历数组的值进行比较
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false
      }
    }
    return true;
  }
}

/**
 *
 * @param timestamp
 * @returns string dd-mm-yy
 */
export const convertTimeStampToddmmyy = (timestamp: number): string => {
  let date = new Date(timestamp)
  const dd = (date.getDate() < 10 ? '0' + date.getDate() : date.getDate()).toString()
  const mm = (date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1).toString()
  const yy = date.getFullYear().toString().slice(-2)
  return `${dd}-${mm}-${yy}`
}

/**
 * cut a array into chunks(different group by the sizes)
 * @param arr
 * @param size
 * @returns
 */
export function chunk(arr, size: number) {
  let result = [];
  for (var i = 0, len = arr.length; i < len; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
}

/**
 * check user have aggree the agreement or not
 * @returns boolean
 */
export const getCookieAgreeementAgreed = (): boolean => {
  //true Customer accepted cookies
  return localStorage.getItem(`cookie_agreement_accept`) === 'true'
}

/**
 * check the local storage have this field or not
 * @param key local storage key
 * @returns boolean
 */
export function localStorageIsValid(key: string): boolean {
  return notNullUndefined(localStorage.getItem(key))
}

/**
 * will used to determine user's locale
 * @returns string
 */
export const getNavigatorLanguage = (): string => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  } else {
    return navigator.language || 'en';
  }
}

export const convertCorrectNumberDisplayFormat = (locales: string, content: number) => {
  return Intl.NumberFormat(locales, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(content)
}

export const convertPriceNumberFormat = (locales: string, content: number) => {
  return Intl.NumberFormat(locales, { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(content)
}

export const convertNumberFormat = (locales: string, content: number) => {
  return Intl.NumberFormat(locales).format(content)
}
export const convertCorrectNumberDisplayFormatNoDecimal = (locales: string, content: number) => {
  return Intl.NumberFormat(locales, { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(content)
}

export const transferDateFormat = (fullDate : Date) : string => {
  if(fullDate) {
    // const year = fullDate.getFullYear() == 1969 || fullDate.getFullYear() == 1970 ? 2001 : fullDate.getFullYear()
    // const month = fullDate.getMonth() + 1 < 10 ? '0' + (fullDate.getMonth() + 1) : fullDate.getMonth() + 1
    // const day = fullDate.getDate() < 10 ? '0' + fullDate.getDate() : fullDate.getDate()
    const year = fullDate.getFullYear()
    if(year <= 1970) return null
    const month = fullDate.getMonth() + 1 < 10 ? '0' + (fullDate.getMonth() + 1) : fullDate.getMonth() + 1
    const day = fullDate.getDate() < 10 ? '0' + fullDate.getDate() : fullDate.getDate()
    return year + '-' + month + '-' +  day
  }
  else {
    return null
  }
}

/**
 * rounding for the numebr
 * @param num number need to/before rounding
 * @param n round to x decimal
 * @returns number after round
 */
//https://www.cnblogs.com/ranyonsue/p/14626787.html
export const fomatFloat = (num, n: number): string => {
  var f = parseFloat(num);
  if (isNaN(f)) {
    return "NaN";
  }
  f = Math.round(num * Math.pow(10, n)) / Math.pow(10, n); // n 幂
  var s = f.toString();
  var rs = s.indexOf('.');
  //判定如果是整数，增加小数点再补0
  if (rs < 0) {
    rs = s.length;
    s += '.';
  }
  while (s.length <= rs + n) {
    s += '0';
  }
  return s;
}

export const getMonthShortcut = (index: number) => {
  switch(index) {
    case 0: return 'Jan'
    case 1: return 'Feb'
    case 2: return 'Mar'
    case 3: return 'Apr'
    case 4: return 'May'
    case 5: return 'Jun'
    case 6: return 'Jul'
    case 7: return 'Aug'
    case 8: return 'Sep'
    case 9: return 'Oct'
    case 10: return 'Nov'
    case 11: return 'Dec'
  }
}

export const checkSOHValid = (result :PoUiTable1StyleAndContent) : Number => {
  return result.font_color == 'red' ? 0 : result.content? Number(result.content.split(',').join('')) : 0
}


export const returnDate = (originalDate, days) => {
  const ms = days * 24 * 60 * 60 * 1000
  const res = originalDate.getTime() + ms
  return transferDateFormat(new Date(res))
}

export const transferDateFormatPOEmail = (fullDate : Date) : string => {
  if(fullDate) {
    // const year = fullDate.getFullYear() == 1969 || fullDate.getFullYear() == 1970 ? 2001 : fullDate.getFullYear()
    // const month = fullDate.getMonth() + 1 < 10 ? '0' + (fullDate.getMonth() + 1) : fullDate.getMonth() + 1
    // const day = fullDate.getDate() < 10 ? '0' + fullDate.getDate() : fullDate.getDate()
    const year = fullDate.getFullYear()
    if(year <= 1970) return null
    const month = fullDate.getMonth() + 1 < 10 ? '0' + (fullDate.getMonth() + 1) : fullDate.getMonth() + 1
    const day = fullDate.getDate() < 10 ? '0' + fullDate.getDate() : fullDate.getDate()
    return day + '-' + month + '-' +  year
  }
  else {
    return null
  }
}

export const convert=(dateString:string)=> {

  if (!dateString || (dateString.length !== 6 && dateString.length !== 8)) {
    return 'Incoming PO';
  }

  const year = dateString.slice(0, 4);
  const month = dateString.slice(4, 6);
  const day = dateString.slice(6, 8);

  const monthName = getMonthName(parseInt(month, 10));

  return `${monthName} ${day}, ${year}`;
}


export const getMonthName=(month: number): string =>{
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  if (month >= 1 && month <= 12) {
    return months[month - 1];
  } else {
    return 'Invalid Month';
  }
}

export const converTimeStampToYYYYMMdd = (date:any) =>{
  // console.log(date)
  const dtObject = new Date(date);
  const year = dtObject.getFullYear();
  const month = String(dtObject.getMonth() + 1).padStart(2, '0'); // 月份是从0开始的，需要加1
  const day = String(dtObject.getDate()).padStart(2, '0');
  const dateString = `${year}${month}${day}`;
  return dateString;
}

export const convertDateToYYYYMMdd = (date:Date)=>{
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // 月份是从0开始的，需要加1
  const day = String(date.getDate()).padStart(2, '0');
  const dateString = `${year}${month}${day}`;
  return dateString;
}

export const returnFileName = (contentDisposition : string): string => {
  if (contentDisposition) {
    const fileNameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = fileNameRegex.exec(contentDisposition);
    if (matches != null && matches[1]) {
      return matches[1].replace(/['"]/g, '');
    }
  }
  return 'defaultFileName'
}